import type { SanityImageCrop, SanityImageHotspot } from "@ouster/shared";

import { __IS_DEV__ } from "~/features/app/utils";

/**
 * The editorial crop/hotspot of an image plus the dimensions they are relative to.
 * Carried through the param pipeline under the reserved `edit` key and resolved
 * into a `rect` query param; never serialized verbatim.
 */
export type SanityImageEdit = {
  crop?: SanityImageCrop | null;
  hotspot?: SanityImageHotspot | null;
  sourceHeight?: number;
  sourceWidth?: number;
};

export type SanityRect = {
  height: number;
  left: number;
  top: number;
  width: number;
};

export type SanityImageParams = {
  w?: number;
  h?: number;
  q?: number | string;
  dpr?: number;
  ar?: string;
  fit?: string;
  crop?: string;
  rect?: string;
  auto?: string;
  edit?: SanityImageEdit;
  "fp-x"?: number;
  "fp-y"?: number;
  "max-w"?: number;
  [key: string]: SanityImageEdit | string | number | undefined;
};

const defaultParams: SanityImageParams = {
  q: "40",
  auto: "format,compress",
};

const sanityImageWidths = [640, 750, 828, 1080, 1200, 1920, 2048];

/**
 * Hard ceiling on delivered image width, matching the largest responsive
 * candidate above.
 *
 * Without it, a param set carrying `q`/`auto` but no `w` served the asset at
 * its native size: 22.2 GB across 15,063 requests averaging 1.5 MB over
 * 2026-09-14..20, 59% of all png/jpg bytes. Editors upload print-resolution
 * screenshots (`chip_03.png` is 5803x3240 and sits on the homepage), and
 * `hero/adapter.ts` passes `w: asset.oriWidth` verbatim for scroll-sequence
 * frames. Nothing on the site renders wider than 2048 CSS pixels, so anything
 * above this ceiling is bytes no viewport can use.
 *
 * See vault/web.media.md.
 */
export const SANITY_MAX_IMAGE_WIDTH = 2048;

export function isSanityImageUrl(src: string) {
  try {
    const url = new URL(src);
    return url.hostname === "cdn.sanity.io" && url.pathname.startsWith("/images/");
  } catch {
    return false;
  }
}

function normalizeAutoParam(value: unknown) {
  if (typeof value !== "string") {
    return value;
  }

  const values = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  return values.includes("format") ? "format" : values[0];
}

function toFiniteNumber(value: unknown) {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

// Imgix expresses an aspect-ratio crop with the `ar` param (e.g. `ar=496:540&fit=crop`) and serves
// the source at native resolution cropped to that ratio. Sanity's image API has no `ar` param and
// only crops when given explicit `w` AND `h`, so a verbatim `ar` is silently ignored and the native
// ratio is served (visible on fill-fit images such as the productRow product cards). Translate `ar`
// into Sanity `w`/`h`: derive the missing dimension from whichever of `w`/`h` is present, defaulting
// to a sensible fallback width when neither is set (the `src` fallback; srcSet candidates carry real
// widths). Cover-fit images are unaffected visually — CSS `object-cover` fills their box regardless.
/**
 * Caps the delivered width at {@link SANITY_MAX_IMAGE_WIDTH}, and supplies one
 * when the caller asked for no dimension at all.
 *
 * Three cases:
 *
 * - **No `w` and no `h`** — the expensive one. Sanity serves the asset at its
 *   native size, so an untouched 5803x3240 upload ships in full. Gets `w` set
 *   to the ceiling.
 * - **`w` over the ceiling** — clamped, and `h` is scaled with it so an
 *   explicit `w`/`h` pair (which is how this pipeline expresses a ratio crop,
 *   see `resolveSanityAspectRatio`) keeps its ratio. Clamping `w` alone would
 *   silently re-crop the image.
 * - **`h` only** — clamped on its own. Rare, but a tall asset is the same
 *   problem in the other axis.
 *
 * `dpr` is part of the budget, not exempt from it: `buildSanitySrcSet` emits a
 * `dpr: 2` candidate, so `w` is measured as `w * dpr` and the ceiling applies
 * to the pixels actually delivered.
 *
 * Runs after `resolveSanityAspectRatio` (so `ar` has already become `w`/`h`)
 * and before `applySanityCropRect` (so the hotspot fit sees the final target).
 * Clamping preserves the ratio, so the resolved rect is unchanged in shape.
 */
export function clampSanityImageWidth(
  params: SanityImageParams,
  maxWidth: number = SANITY_MAX_IMAGE_WIDTH
): SanityImageParams {
  const dpr = toFiniteNumber(params.dpr) ?? 1;
  // The ceiling is on delivered pixels, so a 2x candidate gets half the budget.
  const maxParamSize = Math.max(1, Math.floor(maxWidth / dpr));

  const w = toFiniteNumber(params.w);
  const h = toFiniteNumber(params.h);

  /**
   * `fit=max` means "fit within w/h, but never enlarge". Without it Sanity
   * upscales, so asking for `w=2048` on a 1000px-wide asset returns a 2048px
   * image LARGER than the original — measured at +150% (webp) and +257% (no
   * webp support, i.e. the raw png path) on a real 1000x1000 production asset.
   *
   * Only applied when no `h` is requested. A `w`+`h` pair means the caller
   * wants those exact dimensions — a ratio crop from `resolveSanityAspectRatio`
   * (which sets `fit=crop`) or an Open Graph image, which must be exactly
   * 1200x630 or platforms reject it. An explicit `fit` is never overridden.
   */
  const capNoUpscale = (next: SanityImageParams): SanityImageParams =>
    params.fit === undefined && !h ? { ...next, fit: "max" } : next;

  if (!w && !h) {
    return capNoUpscale({ ...params, w: maxParamSize });
  }

  if (!w) {
    return h && h > maxParamSize ? { ...params, h: maxParamSize } : params;
  }

  if (w <= maxParamSize) {
    // Already within the ceiling, but a responsive candidate can still exceed
    // the asset's native width (a 1000px asset gets 1200w/1920w/2048w
    // candidates), so the no-upscale guard still applies.
    return capNoUpscale(params);
  }

  const next: SanityImageParams = { ...params, w: maxParamSize };
  if (h) {
    next.h = Math.max(1, Math.round((h * maxParamSize) / w));
  }
  return capNoUpscale(next);
}

function resolveSanityAspectRatio(params: SanityImageParams): SanityImageParams {
  const ar = typeof params.ar === "string" ? params.ar : undefined;
  if (!ar) {
    return params;
  }

  const parts = ar.split(":");
  const arW = Number(parts[0]);
  const arH = Number(parts[1]);
  if (!Number.isFinite(arW) || !Number.isFinite(arH) || arW <= 0 || arH <= 0) {
    return params;
  }

  const next: SanityImageParams = { ...params };
  delete next.ar;

  const w = toFiniteNumber(next.w);
  const h = toFiniteNumber(next.h);

  if (w && !h) {
    next.h = Math.round((w * arH) / arW);
  } else if (h && !w) {
    next.w = Math.round((h * arW) / arH);
  } else if (!w && !h) {
    const fallbackWidth = 1200;
    next.w = fallbackWidth;
    next.h = Math.round((fallbackWidth * arH) / arW);
  }

  next.fit = "crop";
  return next;
}

/**
 * Recover an asset's pixel dimensions from its Sanity asset id, which always
 * encodes them (`image-<hash>-1046x1012-png`). Used when the `metadata.dimensions`
 * projection is missing.
 *
 * @param id A Sanity image asset id.
 * @returns The asset dimensions, or undefined when the id is not parseable.
 */
export function parseSanityAssetDimensions(id?: string | null) {
  const match = /-(\d+)x(\d+)-[a-z]+$/.exec(id || "");

  if (!match) {
    return undefined;
  }

  return { width: Number(match[1]), height: Number(match[2]) };
}

/**
 * Translate the editorial crop (0..1 insets) into a pixel rect on the source asset.
 *
 * @param edit The image edit (crop/hotspot plus source dimensions).
 * @returns The crop rect in source pixels, or undefined when it cannot be resolved.
 */
export function resolveSanityCropRect(edit?: SanityImageEdit): SanityRect | undefined {
  const sourceWidth = toFiniteNumber(edit?.sourceWidth);
  const sourceHeight = toFiniteNumber(edit?.sourceHeight);

  if (!sourceWidth || !sourceHeight) {
    return undefined;
  }

  const crop = edit?.crop || { top: 0, bottom: 0, left: 0, right: 0 };
  const left = Math.round(crop.left * sourceWidth);
  const top = Math.round(crop.top * sourceHeight);
  const width = Math.round(sourceWidth - crop.right * sourceWidth - left);
  const height = Math.round(sourceHeight - crop.bottom * sourceHeight - top);

  // Guard degenerate crops (e.g. `left + right >= 1`) so they never produce a
  // negative or out-of-bounds rect.
  if (width <= 0 || height <= 0) {
    return undefined;
  }

  if (left < 0 || top < 0 || left + width > sourceWidth || top + height > sourceHeight) {
    return undefined;
  }

  return { left, top, width, height };
}

// Narrow the crop rect to the requested aspect ratio, centered on the hotspot and
// clamped inside the crop. Transcribed from `@sanity/image-url@2.1.1`
// `src/urlForImage.ts` `fit()` so our rects match what the Studio preview shows.
function fitSanityCropRect(
  crop: SanityRect,
  edit: SanityImageEdit,
  sourceWidth: number,
  sourceHeight: number,
  targetWidth?: number,
  targetHeight?: number
): SanityRect {
  // Without both dimensions the aspect ratio is unconstrained, so the whole crop is served.
  if (!targetWidth || !targetHeight) {
    return crop;
  }

  const spot = edit.hotspot || { x: 0.5, y: 0.5, width: 1, height: 1 };
  const centerX = spot.x * sourceWidth;
  const centerY = spot.y * sourceHeight;
  const radiusX = (spot.width * sourceWidth) / 2;
  const radiusY = (spot.height * sourceHeight) / 2;
  const hotspot = {
    left: centerX - radiusX,
    top: centerY - radiusY,
    right: centerX + radiusX,
    bottom: centerY + radiusY,
  };

  const desiredAspectRatio = targetWidth / targetHeight;
  const cropAspectRatio = crop.width / crop.height;

  if (cropAspectRatio > desiredAspectRatio) {
    // The crop is wider than requested: cut from the sides.
    const height = Math.round(crop.height);
    const width = Math.round(height * desiredAspectRatio);
    const top = Math.max(0, Math.round(crop.top));
    const hotspotXCenter = Math.round((hotspot.right - hotspot.left) / 2 + hotspot.left);
    let left = Math.max(0, Math.round(hotspotXCenter - width / 2));

    if (left < crop.left) {
      left = crop.left;
    } else if (left + width > crop.left + crop.width) {
      left = crop.left + crop.width - width;
    }

    return { left, top, width, height };
  }

  // The crop is taller than requested: cut from top and bottom.
  const width = crop.width;
  const height = Math.round(width / desiredAspectRatio);
  const left = Math.max(0, Math.round(crop.left));
  const hotspotYCenter = Math.round((hotspot.bottom - hotspot.top) / 2 + hotspot.top);
  let top = Math.max(0, Math.round(hotspotYCenter - height / 2));

  if (top < crop.top) {
    top = crop.top;
  } else if (top + height > crop.top + crop.height) {
    top = crop.top + crop.height - height;
  }

  return { left, top, width, height };
}

// Sanity applies an editorial crop only through `rect`; it is never baked into the
// asset url. Resolve the crop against the (already aspect-ratio-normalized) target
// size and write it onto the params.
function applySanityCropRect(params: SanityImageParams) {
  const edit = params.edit;
  const baseCrop = resolveSanityCropRect(edit);

  if (!edit || !baseCrop) {
    return params;
  }

  const sourceWidth = toFiniteNumber(edit.sourceWidth) as number;
  const sourceHeight = toFiniteNumber(edit.sourceHeight) as number;
  const rect = fitSanityCropRect(
    baseCrop,
    edit,
    sourceWidth,
    sourceHeight,
    toFiniteNumber(params.w),
    toFiniteNumber(params.h)
  );

  // Only emit a rect that actually crops, so uncropped images keep the exact urls
  // they had before (and stay warm in the CDN cache).
  const isEffectiveCrop =
    rect.left !== 0 || rect.top !== 0 || rect.width !== sourceWidth || rect.height !== sourceHeight;

  if (!isEffectiveCrop) {
    return params;
  }

  params.rect = `${rect.left},${rect.top},${rect.width},${rect.height}`;

  // `rect` is applied first and already matches the requested aspect ratio, so a
  // focal-point crop on top of it would crop twice. The focal point still drives
  // CSS `object-position` in the Image component.
  delete params["fp-x"];
  delete params["fp-y"];

  if (params.crop === "focalpoint") {
    delete params.crop;
  }

  return params;
}

function buildSanitySrc(src: string, props?: SanityImageParams) {
  let url: URL;

  try {
    url = new URL(src);
  } catch {
    return src;
  }

  const params = applySanityCropRect(
    clampSanityImageWidth(
      resolveSanityAspectRatio({
        ...defaultParams,
        ...props,
      })
    )
  );

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }

    if (key === "auto") {
      const auto = normalizeAutoParam(value);
      if (auto) {
        url.searchParams.set("auto", String(auto));
      }
      continue;
    }

    // Imgix-only cap; Sanity's responsive candidates set explicit widths.
    if (key === "max-w") {
      continue;
    }

    // Resolved into `rect` above; never a query param itself.
    if (key === "edit") {
      continue;
    }

    url.searchParams.set(key, String(value));
  }

  return url.toString();
}

function buildSanitySrcSet(src: string, props?: SanityImageParams, sizes: number[] = sanityImageWidths) {
  const params: SanityImageParams = {
    ...defaultParams,
    ...props,
  };

  if (params.w || params.h) {
    return `${buildSanitySrc(src, params)} 1x, ${buildSanitySrc(src, { ...params, dpr: 2 })} 2x`;
  }

  return sizes.map((width) => `${buildSanitySrc(src, { ...params, w: width })} ${width}w`).join(", ");
}

/**
 * Build a Sanity image-CDN url from a source and imgix-style props.
 * Only `cdn.sanity.io` image URLs are transformed; any other source is
 * returned unchanged so non-Sanity assets (SVGs, GIFs, Mux stills, etc.)
 * pass through untouched.
 *
 * @param src The image source.
 * @param props The image params (translated to Sanity image-API query params).
 * @returns The Sanity image url, or `src` unchanged when not Sanity-hosted.
 */
export function buildSanityImageSrc(src: string, props?: SanityImageParams) {
  if (isSanityImageUrl(src)) {
    return buildSanitySrc(src, props);
  }

  return src;
}

/**
 * Build an image sourceset from a source and imgix-style props.
 * Only `cdn.sanity.io` image URLs are transformed; any other source is
 * returned unchanged.
 *
 * If the props contain a custom width or height, the sizes
 * will be ignored and the `x` descriptors will be used instead.
 *
 * @param src The image source.
 * @param props The image params.
 * @param sizes The candidate widths to generate.
 * @returns A sourceset for the image, or `src` unchanged when not Sanity-hosted.
 */
export function buildSanityImageSrcSet(src: string, props?: SanityImageParams, sizes: number[] = sanityImageWidths) {
  if (isSanityImageUrl(src)) {
    return buildSanitySrcSet(src, props, sizes);
  }

  return src;
}

export type ImageColor = {
  hex: string;
  red: number;
  blue: number;
  green: number;
};

/**
 * Parse an image's color palette from a JSON string.
 *
 * @param palette A JSON stringified palette.
 * @returns A color palette.
 */
export function parsePalette(palette?: string): ImageColor[] | undefined {
  if (!palette) {
    return;
  }

  try {
    return JSON.parse(palette).colors;
  } catch {
    if (__IS_DEV__) {
      console.warn(`Failed to parse palette: ${palette}`);
    }
  }
}
